import { FC, memo, useEffect, useRef } from 'react';
import { CellContext } from '@tanstack/react-table';
import { DragIcon } from '../DragIcon';
import { getRowPathIndexes } from './utils';
import { TableRowVerticalPositionChangeHandler } from './types';
import { UUIDType } from '../../../utils/randomUniqueKey';

export interface DragVerticalTableRowButtonProps extends CellContext<any, any> {
  onChangePosition?: TableRowVerticalPositionChangeHandler;
  crosshairOffset?: number;
}

export const DragVerticalTableRowButton: FC<DragVerticalTableRowButtonProps> =
  memo((props) => {
    const { row, table, onChangePosition, crosshairOffset = 47 } = props;
    const ref = useRef<HTMLDivElement>(null);
    const isFiltered = table.getState().columnFilters.length > 0;

    useEffect(() => {
      const rowPathIndexes = getRowPathIndexes(row);
      const element = ref.current;
      const tr = element?.closest('tr');
      const crosshairContainer = tr?.closest(
        '.row-drag-target-crosshair-container',
      ) as HTMLDivElement | undefined;
      const crosshair = crosshairContainer?.getElementsByClassName(
        'row-drag-target-crosshair',
      )[0] as HTMLDivElement | undefined;

      if (!element || !tr || !crosshairContainer || !crosshair) {
        if (
          // Only print this error once:
          rowPathIndexes.length === 1 &&
          rowPathIndexes[0] === 0
        ) {
          console.error(
            'Cannot initialize drag handle: cannot find required HTML elements',
          );
        }
        return;
      }

      if (isFiltered) {
        element.draggable = false;
        return;
      }

      const handleDragStart = (event: DragEvent) => {
        event.dataTransfer!.dropEffect = 'move';
        event.dataTransfer!.setDragImage(tr, 0, 0);

        tr.classList.add('is-dragged');
        Object.assign(crosshair.dataset, {
          fromPath: serializePath(rowPathIndexes),
          fromRowId: row.id,
        });
      };

      const handleDragEnd = (event: DragEvent) => {
        tr.classList.remove('is-dragged');
        crosshair.classList.remove('is-visible');
        crosshair.style.top = '0px';

        const wasCancelled = event.dataTransfer!.dropEffect === 'none';
        if (wasCancelled) {
          return;
        }

        const { fromPath, toPath, fromRowId } = crosshair.dataset || {};

        if (fromPath && toPath && toPath !== fromPath) {
          const deserializedFromPath = deserializePath(fromPath);
          const deserializedToPath = deserializePath(toPath);
          if (
            fromPath.length !== toPath.length ||
            fromPath[fromPath.length - 1] + 1 !== toPath[toPath.length - 1]
          ) {
            onChangePosition?.(
              deserializedFromPath[0],
              deserializedToPath[0],
              (fromRowId || '') as UUIDType,
            );
          }
        }
      };

      const handleDragEnter = (event: DragEvent) => {
        crosshair.classList.add('is-visible');

        if (tryTopmostPosition(event)) {
          return;
        }

        const { fromPath, fromRowId } = crosshair.dataset || {};
        if (!fromPath || !fromRowId) {
          return;
        }

        const isRowVisuallyExpanded =
          row.getIsExpanded() && Boolean(row.subRows.length);
        if (isRowVisuallyExpanded) {
          return;
        }

        const rowPath = serializePath(rowPathIndexes);
        const crosshairY =
          tr.getBoundingClientRect().bottom -
          crosshairContainer.getBoundingClientRect().top;

        crosshair.style.top = `${crosshairY}px`;
        crosshair.dataset.toPath = rowPath;
      };

      const handleDragLeave = (event: DragEvent) => {
        tryTopmostPosition(event);
      };

      // Tries to position the crosshair at the topmost position of the table. Returns true if successful, false otherwise.
      const tryTopmostPosition = (event: DragEvent) => {
        if (rowPathIndexes.length === 1 && rowPathIndexes[0] === 0) {
          const { height, top } = tr.getBoundingClientRect();

          if (event.clientY - top < height * 0.5) {
            crosshair.style.top = '0px';
            crosshair.dataset.toPath = '0';

            return true;
          }
        }
        return false;
      };

      // This prevents the native drag image (event.dataTransfer!.setDragImage) from "flying" back to its original position upon release
      const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
      };

      element.draggable = true;
      element.addEventListener('dragstart', handleDragStart);
      element.addEventListener('dragend', handleDragEnd);
      tr.addEventListener('dragenter', handleDragEnter);
      tr.addEventListener('dragleave', handleDragLeave);
      document.body.addEventListener('dragover', handleDragOver);

      return () => {
        element.removeEventListener('dragstart', handleDragStart);
        element.removeEventListener('dragend', handleDragEnd);
        tr.removeEventListener('dragenter', handleDragEnter);
        tr.removeEventListener('dragleave', handleDragLeave);
        document.body.removeEventListener('dragover', handleDragOver);
      };
    }, [table, row, onChangePosition, crosshairOffset, isFiltered]);

    return (
      <DragIcon
        ref={ref}
        size={'small'}
        id={`drag-sort-table-row-handle-${props.row.index}`}
        className="drag-sort-handle"
        isDisabled={isFiltered ? 'filtered' : false}
      />
    );
  });

const delimiter = ',' as const;
const serializePath = (path: number[]) => path.join(delimiter);
const deserializePath = (path: string) => path.split(delimiter).map(Number);
